import { Injectable, inject } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import {
  DefinedTermOccurence,
  DocumentDefinedTerm,
  QueryGetDefinedTermOccurenceByTransactionIdsArgs,
  QueryGetDefinedTermTransactionIdsArgs,
  QueryGetDefinedTermsByDocumentArgs,
} from '../../../generated/graphql-models';
import { DocumentId } from '../../document-viewer/models/document-viewer.models';
import { TransactionId } from '../../market-comparison/models/column.model';
import { GraphQLService } from '../../shared/services/graphql.service';
import {
  GET_DEFINED_TERM_TRANSACTION_IDS,
  GET_DEFINED_TERMS_BY_DOCUMENT,
  GET_DEFINE_TERM_OCCURENCE_BY_TRANSACTIONS,
} from '../constants/deal.queries';
import { DocumentDefinedTermViewModel } from '../models/document-defined-term';
import { TermOccurences } from '../models/term-explorer.model';

const NO_OCCURENCES: TermOccurences = { occurences: {}, synonymGroups: {} };

@Injectable({ providedIn: 'root' })
export class TermExplorerService {
  private readonly graphQlService = inject(GraphQLService);
  private readonly transactionIdsByTerm = new WeakMap<TransactionId[], Map<string, Observable<TransactionId[]>>>();

  public getDocumentDefinedTerms(
    documentShortName: DocumentId,
    libraryTransactionId: string
  ): Observable<DocumentDefinedTermViewModel[]> {
    return this.graphQlService
      .getManyQuery<DocumentDefinedTerm, QueryGetDefinedTermsByDocumentArgs>(
        GET_DEFINED_TERMS_BY_DOCUMENT,
        {
          documentShortName,
          libraryTransactionId,
        }
      )
      .pipe(map((terms) => terms.map((t) => ({ ...t, primaryDomOrder: +t.primaryDomOrder }))));
  }

  /** Counts the documents' terms across the deal set, with each term's synonym ring counts. */
  public getDefinedTermOccurence(
    documentShortNames: DocumentId[],
    documentTransactionIds: TransactionId[],
    transactionIds: TransactionId[],
  ): Observable<TermOccurences> {
    if (!transactionIds?.length && !documentShortNames?.length) {
      return of(NO_OCCURENCES);
    }

    return this.graphQlService
      .getManyQuery<DefinedTermOccurence, QueryGetDefinedTermOccurenceByTransactionIdsArgs>(GET_DEFINE_TERM_OCCURENCE_BY_TRANSACTIONS, {
        documentShortNames,
        documentTransactionIds,
        transactionIds,
        withSynonymGroups: true,
      })
      .pipe(
        map((rows) => ({
          occurences: Object.fromEntries(rows.map((row) => [row._id, row.occurence])),
          synonymGroups: Object.fromEntries(
            rows
              .filter((row) => !!row.synonymGroup)
              .map(({ synonymGroup }) => [
                synonymGroup.id,
                {
                  occurence: synonymGroup.occurence,
                  terms: Object.fromEntries(synonymGroup.terms.map((t) => [t.term, t.occurence])),
                },
              ]),
          ),
        })),
      );
  }

  /** The deals in the set with a document defining the term, fetched once per term until the deal set is replaced. */
  public getTransactionIdsDefiningTerm(definedTerm: string, transactionIds: TransactionId[]): Observable<TransactionId[]> {
    if (!transactionIds?.length) {
      return of([]);
    }

    let requests = this.transactionIdsByTerm.get(transactionIds);
    if (!requests) {
      requests = new Map();
      this.transactionIdsByTerm.set(transactionIds, requests);
    }

    let request = requests.get(definedTerm);
    if (!request) {
      request = this.graphQlService
        .getManyQuery<TransactionId, QueryGetDefinedTermTransactionIdsArgs>(GET_DEFINED_TERM_TRANSACTION_IDS, {
          definedTerm,
          transactionIds,
        })
        .pipe(shareReplay(1));
      requests.set(definedTerm, request);
    }
    return request;
  }
}
